import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useThemeStore } from '../store/themeStore'
import { useVersion } from '../hooks/useVersion'
import { useState, useRef, useEffect } from 'react'
import TenantSwitcher from './TenantSwitcher'
import NotificationBanner from './NotificationBanner'
import TaskListButton from './TaskListButton'
import { Avatar } from './ui'
import { PAGE_SCROLL_ID } from '../utils/pageScroll'
import { logout as logoutAPI, getTenantSubscriptionStatus, startTrial, type TenantSubscriptionStatus } from '../services/api'
import { subscriptionBannerFor } from '../utils/subscriptionBanner'
import { resetPackagingEntitlementCache } from '../hooks/usePackagingEntitlement'
import { errorText } from '../utils/errorText'

// Icons for navigation
const CatalogIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
)

const DeploymentsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
  </svg>
)

const MonitoringIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
)

const DiscoveryIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
)

const BundlesIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
)

const TeamsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
)

const SettingsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
)

const UserCogIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h9m5-4a3 3 0 100 6 3 3 0 000-6z" />
  </svg>
)

const ChevronDownIcon = ({ className = '' }: { className?: string }) => (
  <svg className={`w-4 h-4 transition-transform ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
)

const SunIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
)

const MoonIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
)

const ComputerIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
)

interface NavItemProps {
  to: string
  icon: React.ReactNode
  label: string
  active?: boolean
}

function NavItem({ to, icon, label, active }: NavItemProps) {
  return (
    <Link to={to} className={`nav-item ${active ? 'nav-item-active' : ''}`}>
      {icon}
      {label}
    </Link>
  )
}

// A collapsible section header in the sidebar. It is never highlighted like a NavItem; when the
// current page is one of its children and it is collapsed, the label brightens so the section
// is still findable.
function NavSection({
  icon,
  label,
  expanded,
  active,
  onToggle,
}: {
  icon: React.ReactNode
  label: string
  expanded: boolean
  active: boolean
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      className={`nav-item w-full justify-between ${active && !expanded ? 'text-ftext' : ''}`}
    >
      <span className="flex items-center gap-3">
        {icon}
        {label}
      </span>
      <ChevronDownIcon className={expanded ? 'rotate-180' : ''} />
    </button>
  )
}

function NavSub({ to, label, active }: { to: string; label: string; active: boolean }) {
  return (
    <Link to={to} className={`nav-sub ${active ? 'nav-sub-active' : ''}`}>
      {label}
    </Link>
  )
}

function ThemeToggle() {
  const { theme, setTheme, effectiveTheme } = useThemeStore()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const themes = [
    { value: 'light' as const, label: 'Light', icon: <SunIcon /> },
    { value: 'dark' as const, label: 'Dark', icon: <MoonIcon /> },
    { value: 'system' as const, label: 'System', icon: <ComputerIcon /> },
  ]

  const currentTheme = themes.find(t => t.value === theme) || themes[2]

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg text-theme-text-secondary hover:bg-theme-tertiary hover:text-theme-text-primary transition-colors"
        title={`Theme: ${currentTheme.label}`}
      >
        {effectiveTheme === 'dark' ? <MoonIcon /> : <SunIcon />}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-36 bg-theme-card border border-theme-border-primary rounded-lg shadow-lg z-50">
          {themes.map(({ value, label, icon }) => (
            <button
              key={value}
              onClick={() => {
                setTheme(value)
                setIsOpen(false)
              }}
              className={`w-full flex items-center space-x-2 px-3 py-2 text-sm transition-colors first:rounded-t-lg last:rounded-b-lg ${
                theme === value
                  ? 'bg-blue-600 text-white'
                  : 'text-theme-text-primary hover:bg-theme-tertiary'
              }`}
            >
              {icon}
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function Layout() {
  const { user, tenantContext, logout, isTenantAdmin, hasPermission, canManageSubscription } = useAuthStore()
  const { formatVersion } = useVersion()
  const location = useLocation()
  const navigate = useNavigate()
  const isAdmin = isTenantAdmin()
  const isMSP = tenantContext?.tenant_type === 'msp'
  const canManageApps = hasPermission('manage_apps')
  const canDeploy = hasPermission('deploy_apps')
  const canViewJobs = hasPermission('view_jobs')
  const [deploymentExpanded, setDeploymentExpanded] = useState(
    location.pathname.startsWith('/deployments') || location.pathname.startsWith('/bundles')
  )
  const [settingsExpanded, setSettingsExpanded] = useState(
    location.pathname.startsWith('/settings')
  )
  const [teamsExpanded, setTeamsExpanded] = useState(
    location.pathname.startsWith('/teams')
  )
  const [monitoringExpanded, setMonitoringExpanded] = useState(
    location.pathname.startsWith('/monitoring') || location.pathname === '/events' || location.pathname.startsWith('/jobs')
  )
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [subscriptionStatus, setSubscriptionStatus] = useState<TenantSubscriptionStatus | null>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Fetch subscription status for warning banner
  useEffect(() => {
    if (tenantContext?.tenant_id) {
      getTenantSubscriptionStatus()
        .then(setSubscriptionStatus)
        .catch(() => setSubscriptionStatus(null))
    }
  }, [tenantContext?.tenant_id])

  // Decided in utils/subscriptionBanner.ts so the precedence is one pure function.
  const subscriptionBanner = subscriptionBannerFor(subscriptionStatus)
  // Starting the trial is a subscription action, so the same role that buys does
  // it; anyone else sees the offer and who to ask.
  const canStartTrial = canManageSubscription()
  const [startingTrial, setStartingTrial] = useState(false)
  const [trialError, setTrialError] = useState<string | null>(null)
  const startTrialNow = async () => {
    setStartingTrial(true)
    setTrialError(null)
    try {
      await startTrial()
      // The packaging notice caches the plan's budget; a trial changes it.
      resetPackagingEntitlementCache()
      setSubscriptionStatus(await getTenantSubscriptionStatus())
    } catch (err: any) {
      setTrialError(errorText(err, 'The trial could not be started.'))
    } finally {
      setStartingTrial(false)
    }
  }

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle logout with server-side logging
  const handleLogout = async () => {
    try {
      await logoutAPI()
    } catch {
      // Ignore errors - we'll logout locally anyway
    }
    // To sign-in, not the root: a signed-out root leaves for the product page.
    navigate('/login', { replace: true })
    logout()
  }

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname.startsWith('/apps')
    }
    if (path === '/teams') {
      return location.pathname.startsWith('/teams')
    }
    return location.pathname.startsWith(path)
  }

  return (
    <div className="h-screen flex bg-theme-primary overflow-hidden">
      {/* Left Sidebar. Themed like every other surface (sap-mockup/components/Sidebar.tsx);
          nav-item / nav-sub come from the component layer in index.css. */}
      <aside className="w-64 shrink-0 bg-panel border-r border-line flex flex-col">
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-line">
          <Link to="/" className="flex items-center space-x-3">
            <img src="/aac.png" alt="Agentic Packr" className="w-8 h-8 object-contain" />
            <span className="text-ftext font-semibold text-lg">Agentic Packr</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <NavItem
            to="/"
            icon={<CatalogIcon />}
            label="Catalog"
            active={isActive('/')}
          />

          {/* Operational sections -- visible to IT User + Admin */}
          {canDeploy && (
            <div>
              <NavSection
                icon={<DeploymentsIcon />}
                label="Deployment"
                expanded={deploymentExpanded}
                active={isActive('/deployments') || isActive('/bundles')}
                onToggle={() => setDeploymentExpanded(!deploymentExpanded)}
              />
              {deploymentExpanded && (
                <div className="mt-1 space-y-0.5">
                  <NavSub to="/deployments" label="Intune" active={location.pathname === '/deployments'} />
                  <NavSub to="/bundles" label="Bundles" active={location.pathname.startsWith('/bundles')} />
                </div>
              )}
            </div>
          )}

          {canManageApps && (
            <NavItem
              to="/discovery"
              icon={<DiscoveryIcon />}
              label="Discovery"
              active={isActive('/discovery')}
            />
          )}

          {canViewJobs && (
            <div>
              <NavSection
                icon={<MonitoringIcon />}
                label="Monitoring"
                expanded={monitoringExpanded}
                active={isActive('/monitoring') || isActive('/events') || location.pathname === '/jobs'}
                onToggle={() => setMonitoringExpanded(!monitoringExpanded)}
              />
              {monitoringExpanded && (
                <div className="mt-1 space-y-0.5">
                  <NavSub to="/jobs" label="Background Jobs" active={location.pathname === '/jobs'} />
                  <NavSub to="/events" label="Events" active={location.pathname === '/events'} />
                </div>
              )}
            </div>
          )}

          {/* Admin-only sections */}
          {isAdmin && (
            <>
              <div>
                <NavSection
                  icon={<TeamsIcon />}
                  label="Teams"
                  expanded={teamsExpanded}
                  active={isActive('/teams')}
                  onToggle={() => setTeamsExpanded(!teamsExpanded)}
                />
                {teamsExpanded && (
                  <div className="mt-1 space-y-0.5">
                    <NavSub to="/teams/users" label="Users" active={location.pathname === '/teams/users'} />
                    {isMSP && (
                      <NavSub to="/teams/tenants" label="Managed Tenants" active={location.pathname === '/teams/tenants'} />
                    )}
                  </div>
                )}
              </div>

              <div>
                <NavSection
                  icon={<SettingsIcon />}
                  label="Settings"
                  expanded={settingsExpanded}
                  active={isActive('/settings') && !location.pathname.startsWith('/jobs')}
                  onToggle={() => setSettingsExpanded(!settingsExpanded)}
                />
                {settingsExpanded && (
                  <div className="mt-1 space-y-0.5">
                    <NavSub
                      to="/settings/integrations"
                      label="Integrations"
                      active={location.pathname.includes('/settings/integrations')}
                    />
                    {/* 016-template-model: Templates replaces Deployment Experience now that
                        both wizards have landed. The old page stays ROUTED but unlinked — it is
                        the only editor for branding rows a tenant may already have, and publish
                        still reads them, so deleting it would strand data that is still in use.
                        It goes when branding has fully moved to the on-device config. */}
                    <NavSub
                      to="/settings/templates"
                      label="Templates"
                      active={location.pathname.includes('/settings/templates')}
                    />
                    {/* Device-wide appearance. Separate from Templates because it attaches to a
                        MACHINE rather than to a deployment — every package on that device reads it. */}
                    <NavSub
                      to="/settings/branding"
                      label="Branding"
                      active={location.pathname.includes('/settings/branding')}
                    />
                    <NavSub
                      to="/settings/subscription"
                      label="Subscription"
                      active={location.pathname.includes('/settings/subscription')}
                    />
                    <NavSub
                      to="/settings/security"
                      label="Security"
                      active={location.pathname.includes('/settings/security')}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </nav>

        {/* Version info */}
        <div className="px-4 py-3 border-t border-line text-center">
          <span className="text-xs text-ffaint">{formatVersion()}</span>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="h-16 shrink-0 bg-theme-header border-b border-theme-border-primary flex items-center justify-between px-6">
          {/* Tenant Switcher */}
          <TenantSwitcher />

          <div className="flex items-center space-x-4">
            {/* The task list reads deployment runs and packaging jobs; a user who may see neither
                has nothing for it to show, so it is not rendered and nothing polls. */}
            {(canDeploy || canViewJobs) && <TaskListButton />}

            <ThemeToggle />

            <div className="h-6 w-px bg-theme-border-primary" /> {/* Divider */}

            {/* User Profile Dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                className="flex items-center space-x-3 text-theme-text-secondary hover:bg-theme-tertiary rounded-lg px-3 py-2 transition-colors"
              >
                <Avatar name={user?.full_name || 'User'} size={32} src={user?.avatar_url} />
                <div className="text-right">
                  <p className="text-sm font-medium text-theme-text-primary">{user?.full_name}</p>
                  <p className="text-xs text-theme-text-secondary">
                    {tenantContext?.role?.replace('_', ' ') || 'User'}
                  </p>
                </div>
                <ChevronDownIcon />
              </button>

              {/* Dropdown Menu. The email lives here rather than on the button: the collapsed
                  header has room for a name, and this is where you look to check WHICH account
                  you are signed in as. */}
              {userMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-56 bg-theme-card border border-theme-border-primary rounded-lg shadow-lg z-50 py-1"
                >
                  <div className="border-b border-theme-border-primary px-3 py-2.5">
                    <p className="truncate text-sm font-semibold text-theme-text-primary">{user?.full_name}</p>
                    <p className="truncate text-xs text-theme-text-secondary">{user?.email}</p>
                  </div>

                  <Link
                    to="/account"
                    role="menuitem"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-theme-text-primary hover:bg-theme-tertiary transition-colors"
                  >
                    <UserCogIcon />
                    <span>My account</span>
                  </Link>

                  {isAdmin && (
                    <>
                      <Link
                        to="/teams/users"
                        role="menuitem"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-theme-text-primary hover:bg-theme-tertiary transition-colors"
                      >
                        <TeamsIcon />
                        <span>Users &amp; permissions</span>
                      </Link>
                      <Link
                        to="/settings/integrations"
                        role="menuitem"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-theme-text-primary hover:bg-theme-tertiary transition-colors"
                      >
                        <SettingsIcon />
                        <span>Settings</span>
                      </Link>
                    </>
                  )}

                  <div className="my-1 border-t border-theme-border-primary" />

                  <button
                    role="menuitem"
                    onClick={() => {
                      setUserMenuOpen(false)
                      handleLogout()
                    }}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-red-500 hover:bg-theme-tertiary transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Subscription Warning Banner */}
        {subscriptionBanner && (
          <div
            role="status"
            className={`${subscriptionBanner.color} shrink-0 text-white text-sm text-center py-2 px-4`}
          >
            {subscriptionBanner.message}
            {subscriptionBanner.linkToSubscription && (
              <Link to="/settings/subscription" className="ml-2 underline font-medium">
                Go to subscription
              </Link>
            )}
            {subscriptionBanner.action === 'start-trial' && canStartTrial && (
              <button
                type="button"
                onClick={startTrialNow}
                disabled={startingTrial}
                className="ml-3 px-3 py-0.5 rounded-md bg-white text-blue-700 font-medium hover:bg-blue-50 disabled:opacity-60"
              >
                {startingTrial ? 'Starting…' : 'Start free trial'}
              </button>
            )}
            {subscriptionBanner.action === 'start-trial' && !canStartTrial && (
              <span className="ml-2">The tenant owner can start it from the Subscription page.</span>
            )}
            {trialError && <span className="ml-2 underline">{trialError}</span>}
          </div>
        )}

        {/* Banners raised by whatever is running — a deployment handed to a worker, and how it
            went. Above the content rather than in it, so a page never has to host them. */}
        <NotificationBanner />

        {/* Page Content */}
        <main id={PAGE_SCROLL_ID} className="flex-1 min-h-0 bg-theme-primary overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
